// src/components/coins/CoinStore.js
//
// 🪙 COIN SHOP — buy coin packs with real money (Stripe Checkout).
//   educator=true  → coins go into the CLASS COIN BANK, to give to students
//   otherwise      → (parents) coins go into this account's wallet, for the
//                    child who learns on it
// Students never see this (the button isn't shown to them, and the server
// refuses them).
//
// Props: educator, onClose()

import ModalPortal from "../common/ModalPortal";
import React, { useEffect, useState } from "react";
import { DEFAULT_PACKS, getCoinPacks, money, startCoinCheckout } from "../../services/coinStoreService";
import { useWallet } from "../../services/coinService";
import "./coinStore.css";

// a pile of coins that grows with the pack
const CoinPile = ({ size }) => {
  const n = size >= 1000 ? 14 : size >= 500 ? 10 : size >= 250 ? 7 : 4;
  const spots = [
    [50, 74], [34, 76], [66, 76], [42, 64], [58, 64], [50, 54], [26, 66], [74, 66], [34, 52], [66, 52], [50, 42], [18, 76], [82, 76], [50, 30],
  ].slice(0, n);
  return (
    <svg viewBox="0 0 100 100" className="sbl-cs-pile" aria-hidden="true">
      {spots
        .slice()
        .sort((a, b) => a[1] - b[1])
        .map(([x, y], i) => (
          <g key={i}>
            <ellipse cx={x} cy={y + 3} rx="13" ry="6" fill="#c98a00" />
            <ellipse cx={x} cy={y} rx="13" ry="6" fill="#ffd23f" stroke="#b07a00" strokeWidth="1.5" />
            <ellipse cx={x} cy={y} rx="8" ry="3.4" fill="none" stroke="#f0a500" strokeWidth="1.4" />
          </g>
        ))}
      <path d="M78 26 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2 z" fill="#fff" />
    </svg>
  );
};

const CoinStore = ({ educator = false, onClose }) => {
  const wallet = useWallet();
  const [packs, setPacks] = useState(DEFAULT_PACKS);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    getCoinPacks().then(setPacks);
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const buy = async (p) => {
    setBusy(p.id);
    setErr("");
    try {
      await startCoinCheckout(p.id, window.location.pathname);
    } catch (e) {
      setErr(e?.message || "Checkout couldn't start. Please try again.");
      setBusy("");
    }
  };

  return (
    <ModalPortal>
      <div className="sbl-cs-pop" role="dialog" aria-modal="true" aria-label="Coin shop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
        <div className="sbl-cs">
          <div className="sbl-cs-ribbon">{educator ? "Class Coin Bank" : "Coin Shop"}</div>
          <button type="button" className="sbl-cs-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
          <p className="sbl-cs-lead">
            {educator ? (
              <>
                Coins you buy go into your <b>class coin bank</b> — <b>{(wallet.classBank || 0).toLocaleString()}</b> now. Hand them out from <b>Students → Give coins → From class bank</b>.
              </>
            ) : (
              <>
                Add coins to your child's account. They spend them on avatar clothes, glasses, pets and more — on top of the coins they earn by learning.
              </>
            )}
          </p>
          <div className="sbl-cs-packs">
            {packs.map((p) => (
              <div key={p.id} className={`sbl-cs-pack ${p.best ? "is-best" : ""}`}>
                {p.best && <span className="sbl-cs-badge">Best value</span>}
                {p.saleCents && p.saleCents < p.cents && <span className="sbl-cs-badge is-sale">{Math.round(100 - (p.saleCents * 100) / p.cents)}% off</span>}
                <CoinPile size={p.coins} />
                <b className="sbl-cs-coins">{p.coins.toLocaleString()}</b>
                <span className="sbl-cs-unit">coins</span>
                {educator && <span className="sbl-cs-per">≈ {Math.floor(p.coins / 10)} coins each for 10 students</span>}
                <button type="button" className="sbl-cs-buy" onClick={() => buy(p)} disabled={Boolean(busy)}>
                  {busy === p.id ? (
                    <span className="sbl-cs-spin" aria-label="Opening checkout" />
                  ) : p.saleCents && p.saleCents < p.cents ? (
                    <>
                      <s className="sbl-cs-was">{money(p.cents)}</s> {money(p.saleCents)}
                    </>
                  ) : (
                    money(p.cents)
                  )}
                </button>
              </div>
            ))}
          </div>
          {err && <p className="sbl-cs-err">{err}</p>}
          <p className="sbl-cs-fine">Secure checkout by Stripe. One-time purchase — not a subscription. Coins can't be exchanged for money.</p>
        </div>
      </div>
    </ModalPortal>
  );
};

export default CoinStore;
